import { z } from 'zod';
import { markoMediaPlanDataZ, } from './artifacts/marko-media-plan.zod.js';
import { isAcceptedArtifactStatus, isReviewableArtifactStatus } from './domain.js';
import { responseLocaleSchema } from './locale.js';
export const MCP_MATERIAL_REVIEW_VERSION = 'rw-material-review-v2';
const artifactReferenceSchema = z
    .object({ artifactId: z.uuid(), artifactVersion: z.number().int().positive() })
    .strict();
const previewIdSchema = z
    .string()
    .min(22)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/u);
function isSecurePreviewUrl(value) {
    const url = new URL(value);
    if (url.protocol === 'https:')
        return true;
    return (url.protocol === 'http:' &&
        (url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]'));
}
const localizedCopySchema = z
    .object({
    locale: responseLocaleSchema,
    primaryText: z.string().trim().min(1).max(5_000),
    headline: z.string().trim().min(1).max(500),
    cta: z.string().trim().min(1).max(200),
})
    .strict();
export const mcpMaterialReviewCreativeSchema = z
    .object({
    id: z.string().trim().min(1).max(200),
    placement: z.string().trim().min(1).max(200),
    media: z
        .object({
        kind: z.enum(['image', 'video', 'text']),
        url: z.url().nullable(),
    })
        .strict(),
    localizedCopies: z.array(localizedCopySchema).min(1).max(2),
    generation: z
        .object({
        aiGenerated: z.boolean(),
        disclosureRequired: z.boolean().nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((creative, context) => {
    const locales = creative.localizedCopies.map(({ locale }) => locale);
    if (new Set(locales).size !== locales.length) {
        context.addIssue({
            code: 'custom',
            path: ['localizedCopies'],
            message: 'Locales must be unique',
        });
    }
    if (creative.media.kind === 'text' && creative.media.url !== null) {
        context.addIssue({
            code: 'custom',
            path: ['media', 'url'],
            message: 'Text previews cannot carry an asset URL',
        });
    }
    if (creative.media.kind !== 'text' && creative.media.url === null) {
        context.addIssue({
            code: 'custom',
            path: ['media', 'url'],
            message: 'Visual previews require an asset URL',
        });
    }
    if (creative.media.url && !creative.media.url.startsWith('https://')) {
        context.addIssue({
            code: 'custom',
            path: ['media', 'url'],
            message: 'Preview assets must use HTTPS',
        });
    }
});
export const mcpMaterialReviewPlatformSchema = z.enum([
    'instagram',
    'facebook',
    'google_search',
    'yandex_direct',
    'tiktok',
    'youtube',
]);
const reviewPlatformSchema = z
    .object({
    platform: mcpMaterialReviewPlatformSchema,
    selection: z.enum(['selected', 'not_selected']),
    campaignIds: z.array(z.string().trim().min(1).max(100)).max(20),
    creatives: z.array(mcpMaterialReviewCreativeSchema).length(3),
})
    .strict()
    .superRefine((platform, context) => {
    if (platform.selection === 'selected' && platform.campaignIds.length === 0) {
        context.addIssue({
            code: 'custom',
            path: ['campaignIds'],
            message: 'Selected platforms require a source campaign',
        });
    }
    if (platform.selection === 'not_selected' && platform.campaignIds.length > 0) {
        context.addIssue({
            code: 'custom',
            path: ['campaignIds'],
            message: 'Unselected platforms cannot cite a selected campaign',
        });
    }
    if ((platform.platform === 'tiktok' || platform.platform === 'youtube') &&
        platform.creatives.some(({ media }) => media.kind !== 'video')) {
        context.addIssue({
            code: 'custom',
            path: ['creatives'],
            message: 'TikTok and YouTube are available only with video previews',
        });
    }
});
export const mcpMaterialReviewLandingPageSchema = z
    .object({
    previewId: previewIdSchema,
    authenticatedUrl: z.url(),
    publicUrl: z.null(),
    access: z.literal('authenticated_owner_only'),
    formMode: z.literal('test'),
    viewports: z.tuple([z.literal('phone'), z.literal('desktop')]),
    locales: z
        .array(responseLocaleSchema)
        .min(1)
        .max(2),
    content: z
        .object({
        sourceCampaignId: z.string().trim().min(1).max(100),
        sourceCreativeRequirementId: z.string().trim().min(1).max(100),
        sourceDirectionId: z.string().trim().min(1).max(100),
        sections: z.tuple([
            z
                .object({ section: z.literal('hero'), text: z.string().trim().min(1).max(10_000) })
                .strict(),
            z
                .object({ section: z.literal('proof'), text: z.string().trim().min(1).max(10_000) })
                .strict(),
            z
                .object({
                section: z.literal('objections'),
                text: z.string().trim().min(1).max(10_000),
            })
                .strict(),
            z
                .object({ section: z.literal('form'), text: z.string().trim().min(1).max(10_000) })
                .strict(),
        ]),
        callToAction: z.string().trim().min(1).max(200),
    })
        .strict(),
})
    .strict()
    .superRefine((landingPage, context) => {
    if (!isSecurePreviewUrl(landingPage.authenticatedUrl)) {
        context.addIssue({
            code: 'custom',
            path: ['authenticatedUrl'],
            message: 'Preview URL must use HTTPS outside a loopback development environment',
        });
    }
    if (!landingPage.authenticatedUrl.includes(landingPage.previewId)) {
        context.addIssue({
            code: 'custom',
            path: ['authenticatedUrl'],
            message: 'Preview URL must contain its opaque identifier',
        });
    }
    if (new Set(landingPage.locales).size !== landingPage.locales.length) {
        context.addIssue({
            code: 'custom',
            path: ['locales'],
            message: 'Landing page locales must be unique',
        });
    }
});
const directionSchema = z
    .object({
    id: z.string().trim().min(1).max(100),
    title: z.string().trim().min(1).max(300),
    countryCode: z.string().trim().length(2).toUpperCase(),
    landingPage: mcpMaterialReviewLandingPageSchema,
    platforms: z.array(reviewPlatformSchema).min(4).max(6),
    bilingualReview: z
        .object({
        status: z.enum(['not_applicable', 'passed']),
        reviewedAt: z.iso.datetime({ offset: true }).nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((direction, context) => {
    const platforms = direction.platforms.map(({ platform }) => platform);
    if (new Set(platforms).size !== platforms.length) {
        context.addIssue({
            code: 'custom',
            path: ['platforms'],
            message: 'Platforms must be unique within a direction',
        });
    }
    for (const required of ['instagram', 'facebook', 'google_search', 'yandex_direct']) {
        if (!platforms.includes(required)) {
            context.addIssue({
                code: 'custom',
                path: ['platforms'],
                message: `Missing review platform: ${required}`,
            });
        }
    }
    if (direction.countryCode !== 'KZ')
        return;
    const landingLocales = new Set(direction.landingPage.locales);
    if (!landingLocales.has('kk') || !landingLocales.has('ru')) {
        context.addIssue({
            code: 'custom',
            path: ['landingPage', 'locales'],
            message: 'Kazakhstan landing pages require Kazakh and Russian',
        });
    }
    for (const [platformIndex, platform] of direction.platforms.entries()) {
        for (const [creativeIndex, creative] of platform.creatives.entries()) {
            const locales = new Set(creative.localizedCopies.map(({ locale }) => locale));
            if (!locales.has('kk') || !locales.has('ru')) {
                context.addIssue({
                    code: 'custom',
                    path: ['platforms', platformIndex, 'creatives', creativeIndex, 'localizedCopies'],
                    message: 'Kazakhstan ads require Kazakh copy with a Russian translation',
                });
            }
        }
    }
    if (direction.bilingualReview.status !== 'passed' || !direction.bilingualReview.reviewedAt) {
        context.addIssue({
            code: 'custom',
            path: ['bilingualReview'],
            message: 'Kazakh copy requires an independent bilingual review',
        });
    }
});
const materialReviewPayloadSchema = z
    .object({
    sourceMediaPlan: artifactReferenceSchema,
    sourceCreatives: artifactReferenceSchema,
    createdAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }),
    previewPolicy: z
        .object({
        ttlDays: z.literal(14),
        minimumEntropyBits: z.literal(128),
        publicUrlAvailable: z.literal(false),
    })
        .strict(),
    directions: z.array(directionSchema).min(1).max(20),
    safety: z
        .object({
        generatedTestimonialsPresent: z.literal(false),
        generatedPeopleInRestrictedRolesPresent: z.literal(false),
        restrictedRoles: z.tuple([z.literal('customer'), z.literal('doctor'), z.literal('expert')]),
    })
        .strict(),
})
    .strict();
function validateMaterialReviewWindow(review, context) {
    if (Date.parse(review.expiresAt) - Date.parse(review.createdAt) !== 14 * 24 * 60 * 60 * 1_000) {
        context.addIssue({
            code: 'custom',
            path: ['expiresAt'],
            message: 'Review previews must expire after exactly 14 days',
        });
    }
    const directionIds = review.directions.map(({ id }) => id);
    if (new Set(directionIds).size !== directionIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['directions'],
            message: 'Direction identifiers must be unique',
        });
    }
}
export const materialReviewMetadataSchema = materialReviewPayloadSchema
    .extend({ schemaVersion: z.literal(2) })
    .strict()
    .superRefine(validateMaterialReviewWindow);
export const mcpMaterialReviewSchema = materialReviewPayloadSchema
    .extend({
    methodologyVersion: z.literal(MCP_MATERIAL_REVIEW_VERSION),
    artifact: z
        .object({
        id: z.uuid(),
        version: z.number().int().positive(),
        status: z.enum(['draft', 'in_review', 'pending_review', 'pending_approval']),
    })
        .strict(),
    acceptance: z
        .object({
        action: z.literal('create_creatives_version'),
        artifactType: z.literal('creatives'),
        artifactId: z.uuid(),
        baseVersion: z.number().int().positive(),
        createsProviderAccount: z.literal(false),
        createsCampaign: z.literal(false),
    })
        .strict(),
})
    .strict()
    .superRefine(validateMaterialReviewWindow);
const PLATFORM_PROVIDER = {
    instagram: 'meta_ads',
    facebook: 'meta_ads',
    google_search: 'google_ads',
    yandex_direct: 'yandex_direct',
    tiktok: 'tiktok_ads',
    youtube: 'google_ads',
};
function sourceMatches(artifact, mediaPlan) {
    const source = Reflect.get(artifact.data, 'sourceMediaPlan');
    return Boolean(source &&
        typeof source === 'object' &&
        !Array.isArray(source) &&
        Reflect.get(source, 'artifactId') === mediaPlan.id &&
        Reflect.get(source, 'artifactVersion') === mediaPlan.version);
}
function bindingsMatchMediaPlan(review, mediaPlan) {
    const campaigns = new Map(mediaPlan.campaigns.map((campaign) => [campaign.id, campaign]));
    return review.directions.every((direction) => {
        const content = direction.landingPage.content;
        const sourceCampaign = campaigns.get(content.sourceCampaignId);
        const sourceRequirement = sourceCampaign?.creativeRequirements.find(({ id }) => id === content.sourceCreativeRequirementId);
        const sourceDraft = sourceRequirement?.landingPageDraft;
        if (!sourceDraft ||
            sourceDraft.directionId !== content.sourceDirectionId ||
            sourceDraft.callToAction !== content.callToAction ||
            sourceDraft.sections.some((section, index) => section.section !== content.sections[index]?.section ||
                section.text !== content.sections[index]?.text)) {
            return false;
        }
        return direction.platforms.every((platform) => platform.campaignIds.every((campaignId) => campaigns.get(campaignId)?.provider === PLATFORM_PROVIDER[platform.platform]));
    });
}
export function deriveMcpMaterialReview(artifacts, canonicalMediaPlanArtifactId, evaluatedAt) {
    if (!canonicalMediaPlanArtifactId)
        return null;
    const mediaPlan = artifacts.find(({ id, type, status }) => id === canonicalMediaPlanArtifactId &&
        type === 'media_plan' &&
        isAcceptedArtifactStatus(status));
    if (!mediaPlan)
        return null;
    const parsedMediaPlan = markoMediaPlanDataZ.safeParse(mediaPlan.data);
    if (!parsedMediaPlan.success)
        return null;
    const candidates = [...artifacts]
        .filter((artifact) => artifact.type === 'creatives' &&
        isReviewableArtifactStatus(artifact.status) &&
        sourceMatches(artifact, mediaPlan))
        .sort((left, right) => right.version - left.version || right.updatedAt.localeCompare(left.updatedAt));
    const artifact = candidates[0];
    if (!artifact)
        return null;
    const parsed = materialReviewMetadataSchema.safeParse(Reflect.get(artifact.metadata, 'materialReview'));
    if (!parsed.success)
        return null;
    const review = parsed.data;
    if (review.sourceMediaPlan.artifactId !== mediaPlan.id ||
        review.sourceMediaPlan.artifactVersion !== mediaPlan.version ||
        review.sourceCreatives.artifactId !== artifact.id ||
        review.sourceCreatives.artifactVersion !== artifact.version ||
        Date.parse(review.createdAt) > evaluatedAt.getTime() ||
        Date.parse(review.expiresAt) <= evaluatedAt.getTime() ||
        !bindingsMatchMediaPlan(review, parsedMediaPlan.data)) {
        return null;
    }
    return mcpMaterialReviewSchema.parse({
        methodologyVersion: MCP_MATERIAL_REVIEW_VERSION,
        sourceMediaPlan: review.sourceMediaPlan,
        sourceCreatives: review.sourceCreatives,
        createdAt: review.createdAt,
        expiresAt: review.expiresAt,
        previewPolicy: review.previewPolicy,
        directions: review.directions,
        safety: review.safety,
        artifact: { id: artifact.id, version: artifact.version, status: artifact.status },
        acceptance: {
            action: 'create_creatives_version',
            artifactType: 'creatives',
            artifactId: artifact.id,
            baseVersion: artifact.version,
            createsProviderAccount: false,
            createsCampaign: false,
        },
    });
}
//# sourceMappingURL=mcp-material-review.js.map